import styled, { useTheme } from 'styled-components';
import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';

import {
  BANK_ICONS,
  BANK_LABELS,
  OptionList,
  Typography,
  useStatusInfo,
} from '@libs/components';
import {
  CurrencyType,
  FiatCurrencySymbol,
  RealFiatAssets,
  TransactionStatusType,
  TransactionType,
} from '@libs/types';
import { formatDate } from '@libs/hooks';
import {
  convertExponentialToDecimal,
  formatCryptoAmount,
  shortenAddress,
} from '@libs/helpers';

import { getTransactionDetailsQuery } from 'src/gql';
import {
  useApiResponse,
  useCardDetector,
  useModal,
  useTranslation,
} from 'src/hooks';
import { getAlternativeAssetLabel, hashCardMask } from 'src/helpers';
import {
  TRANSACTION_LABELS,
  TRANSACTION_OPERATIONS_TITLE,
  TRANSACTION_STATUS_FILTER_ICONS,
  USDT_PRICE,
} from 'src/constants';
import { DASHBOARD_NAV_WIDTH } from 'src/components/dashboard-nav/DashboardNav';
import { InfoFieldDescription, TransactionDetailsHeader } from 'src/components';

export const useTransactionsModalGe = (
  modalLeftSpace = DASHBOARD_NAV_WIDTH
) => {
  const { Dialog, openModal } = useModal();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { getStatusData } = useStatusInfo();
  const { cardDetector } = useCardDetector();
  const [transactionDetails, { data: DetailsData, loading }] = useLazyQuery(
    getTransactionDetailsQuery
  );

  const { response } = useApiResponse({
    data: DetailsData,
    loading,
  });

  const data = useMemo(
    () => response?.getTransactionDetails?.[0]?.details,
    [response]
  );

  const handleTransactionDetails = useCallback(
    (index: number) => {
      transactionDetails({
        variables: { record: { transactionId: index } },
      });
    },
    [transactionDetails]
  );

  const modifiedDate = useMemo(() => {
    const date = new Date(data?.date || '');

    return formatDate('D MMM YYYY HH:mm', date);
  }, [data]);

  const infoFields = useMemo(() => {
    const tempData = [];
    const transactionStatus =
      data?.status === TransactionStatusType.DRAFT
        ? getStatusData(TransactionStatusType.PENDING)
        : getStatusData(data?.status);

    const shouldCopyButtonHide = [
      CurrencyType.GEL,
      CurrencyType.EUR,
      CurrencyType.USD,
    ].includes(data?.assetCode as CurrencyType);

    const amount = data?.amount || 0;

    const amountToShow =
      TransactionType.BonusClaim === data?.type
        ? formatCryptoAmount(amount, USDT_PRICE, 2)
        : amount;

    const BankImage = data?.senderBankCode
      ? BANK_ICONS[data.senderBankCode]
      : () => <></>;

    const WithdrawBankImage = data?.recipientBankCode
      ? BANK_ICONS[data.recipientBankCode]
      : () => <></>;

    if (data) {
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('table_status')}
          </Typography>
        ),
        description: (
          <FlexContainer>
            {TRANSACTION_STATUS_FILTER_ICONS[data?.status]}
            <Typography
              type="subHeadline"
              fontFamily="primaryMedium"
              color={transactionStatus.colors[0]}
            >
              {t(transactionStatus.text)}
            </Typography>
          </FlexContainer>
        ),
      });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_details_transactions_type')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {t(TRANSACTION_LABELS[data.type])}
          </Typography>
        ),
      });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_invoice_id')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {data.internalOperationId}
          </Typography>
        ),
      });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_invoice_time')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {modifiedDate}
          </Typography>
        ),
      });

      if (data.type === TransactionType.Sell) {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_sold_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.fromAmount} ${
                FiatCurrencySymbol[data.fromAsset?.name as RealFiatAssets] ||
                data.fromAsset?.name
              }`}
            </Typography>
          ),
        });
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_recived_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.toAmount} ${
                FiatCurrencySymbol[data.toAsset?.name as RealFiatAssets] ||
                data.toAsset?.name
              }`}
            </Typography>
          ),
        });
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_fee')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${convertExponentialToDecimal(data.fee)} ${
                FiatCurrencySymbol[
                  getAlternativeAssetLabel(data.feeAssetCode) as RealFiatAssets
                ] || getAlternativeAssetLabel(data.feeAssetCode)
              }`}
            </Typography>
          ),
        });
      } else if (data.type === TransactionType.Buy) {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_recived_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${amountToShow} ${
                FiatCurrencySymbol[
                  getAlternativeAssetLabel(data.assetCode) as RealFiatAssets
                ] || getAlternativeAssetLabel(data.assetCode)
              }`}
            </Typography>
          ),
        });
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_spent_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.fromAmount} ${
                FiatCurrencySymbol[data.fromAsset?.name as RealFiatAssets] ||
                data.fromAsset?.name
              }`}
            </Typography>
          ),
        });
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_fee')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.fee} ${
                FiatCurrencySymbol[
                  getAlternativeAssetLabel(data.feeAssetCode) as RealFiatAssets
                ] || getAlternativeAssetLabel(data.feeAssetCode)
              }`}
            </Typography>
          ),
        });
      } else if (data.type === TransactionType.Deposit) {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_recived_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {data.amount}
            </Typography>
          ),
        });
        !!data.sourceAddress &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('common_source_address')}
              </Typography>
            ),
            description: (
              <InfoFieldDescription
                shouldCopyButtonHide={shouldCopyButtonHide}
                shouldShorten={true}
                data={data.sourceAddress as string}
              />
            ),
          });
        !!data.network &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('transaction_invoice_network')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {data.network}
              </Typography>
            ),
          });
        !!data.senderAccountNumber &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('account_info_title')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {data.senderAccountNumber}
              </Typography>
            ),
          });
        !!data.senderBankCode &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('account_info_title')}
              </Typography>
            ),
            description: (
              <>
                <Typography type="bodyButton" fontFamily="primaryMedium">
                  {t(BANK_LABELS[data.senderBankCode])}{' '}
                </Typography>
                <BankIconWrapper>
                  <BankImage />
                </BankIconWrapper>
              </>
            ),
          });
        !!data.senderCardNumber &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('common_card')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {cardDetector(data.senderCardNumber).cardProvider}{' '}
                {hashCardMask(data.senderCardNumber)}
                <CardIcon src={cardDetector(data.senderCardNumber).cardIcon} />
              </Typography>
            ),
          });
      } else if (data.type === TransactionType.Withdrawal) {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_send_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {data.amount}{' '}
              {FiatCurrencySymbol[data.assetCode as RealFiatAssets] ||
                data.assetCode}
            </Typography>
          ),
        });
        !!data.destination &&
          !!data.sourceAddress &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('transaction_details_reciver_address')}
              </Typography>
            ),
            description: (
              <InfoFieldDescription
                shouldCopyButtonHide={shouldCopyButtonHide}
                shouldShorten={true}
                data={data.destination as string}
              />
            ),
          });
        !!data.blockchainTransactionId &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('transaction_details_hash')}
              </Typography>
            ),
            description: (
              <InfoFieldDescription
                shouldCopyButtonHide={shouldCopyButtonHide}
                shouldShorten={true}
                data={data.blockchainTransactionId as string}
              />
            ),
          });
        !!data.network &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('transaction_invoice_network')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {data.network}
              </Typography>
            ),
          });

        !!data.recipientBankCode &&
          !data.recipientCardNumber &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('withdraw_bank_name')}
              </Typography>
            ),
            description: (
              <>
                <Typography type="bodyButton" fontFamily="primaryMedium">
                  {t(BANK_LABELS[data.recipientBankCode])}{' '}
                </Typography>
                <BankIconWrapper>
                  <WithdrawBankImage />
                </BankIconWrapper>
              </>
            ),
          });
        !!data.recipientCardNumber &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('common_card')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {cardDetector(data.recipientCardNumber).cardProvider}{' '}
                {hashCardMask(data.recipientCardNumber)}
                <CardIcon
                  src={cardDetector(data.recipientCardNumber).cardIcon}
                />
              </Typography>
            ),
          });
        !!data.recipientAccountNumber &&
          tempData.push({
            title: (
              <Typography
                type="bodyButton"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('account_info_title')}
              </Typography>
            ),
            description: (
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {shortenAddress(data.recipientAccountNumber)}
              </Typography>
            ),
          });
      } else {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_details_recived_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {data.amount}
            </Typography>
          ),
        });
      }
    }

    return tempData;
  }, [
    data,
    getStatusData,
    colors.text.secondary,
    t,
    modifiedDate,
    cardDetector,
  ]);

  const transactionModal = useMemo(() => {
    if (!data) return;

    return (
      <Dialog
        leftSpace={modalLeftSpace}
        titleIcon={<AssetIcon src={data.assetIconUrl} />}
        title={data.assetLabel}
      >
        <TransactionDetails>
          <TransactionDetailsHeader
            assetIcone={data.fromAsset?.iconUrl ?? data.assetIconUrl}
            toAssetIcone={data.toAsset?.iconUrl}
            title={t(TRANSACTION_LABELS[data.type])}
            amount={
              data.fromAmount
                ? data.fromAmount.toString()
                : data.amount.toString()
            }
            assetCode={
              FiatCurrencySymbol[
                getAlternativeAssetLabel(
                  (data.fromAsset?.code ?? data.assetCode) as CurrencyType
                ) as RealFiatAssets
              ] ||
              getAlternativeAssetLabel(
                (data.fromAsset?.code ?? data.assetCode) as CurrencyType
              )
            }
            operationTitle={t(TRANSACTION_OPERATIONS_TITLE[data.type])}
            toAssetCode={
              FiatCurrencySymbol[
                getAlternativeAssetLabel(
                  data.toAsset?.code as CurrencyType
                ) as RealFiatAssets
              ] || getAlternativeAssetLabel(data.toAsset?.code as CurrencyType)
            }
            toAmount={data.toAmount}
          />
          {/* <StatusWrapper>
            <Typography
              type="subHeadline"
              fontFamily="primaryBold"
              color={transactionStatus.colors[0]}
            >
              {t(transactionStatus.text)}
            </Typography>
          </StatusWrapper> */}
          <CustomOptionList options={infoFields} fontType="bodyButton" />
          {/* TODO: implement PDF download */}
          {/* <DownloadButton title={t('transaction_receipt')} /> */}
        </TransactionDetails>
      </Dialog>
    );
  }, [Dialog, infoFields, data, modalLeftSpace, t]);

  return {
    transactionModal,
    openModal,
    handleTransactionDetails,
  };
};
const AssetIcon = styled.img`
  width: 3rem;
  height: 3rem;
`;
const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

const CustomOptionList = styled(OptionList)`
  width: 100%;
  padding: 0;
  margin-bottom: 0;
  border: none;
  padding-left: 1.6rem;
  .text-wrapper {
    /* width: unset; */
  }
  .description-wrapper {
    padding-right: 1.6rem;
  }
  .description {
    width: max-content;
  }
`;
const TransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.8rem 2.2rem 3.2rem;
  max-height: 50rem;

  ${({ theme }) => theme.media.tablet`
   max-height: initial;
  `};

  ${({ theme }) => theme.media.laptop`
    max-width: 50rem;
    padding: 2.8rem 1.6rem 2.4rem;
    min-width: 47rem;
  `};
`;
export const CardIcon = styled.img`
  width: 2.6rem;
  margin-left: 0.8rem;
`;
export const BankIconWrapper = styled.div`
  width: 2.6rem;
  margin-left: 0.8rem;
  svg {
    width: 100%;
  }
`;
